/** Booked & order — the booking's state, references, role, package and tags (editable),
 *  its identity and money as the reservation system sent them (read-only), and the
 *  system's provenance with the linked order. The tickets, the related-booking tree and
 *  the package siblings sit behind disclosures (they are grids, not fields).
 *
 *  The tabs view splits the stage over three tabs (Booking, Money, Integration) and
 *  renders each part with this panel: the tickets follow the `tickets` field, the links
 *  and related bookings the linked `order`. */
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { Box, Button, Collapse, Link, Skeleton, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useId, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { BookingRecordStage } from '@/api/bookingRecord';
import * as ordersApi from '@/api/orders';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';
import type { ViewFieldRef, ViewSection } from '@/features/records/views/types';
import { usePermissions } from '@/hooks/usePermissions';

import { BookingFamilyTree } from '../../overview/BookingFamilyTree';
import { orderActions } from '../../overview/bookingLinks';
import { PackageBookingsGrid } from '../../overview/PackageBookingsGrid';
import { TicketsGrid } from '../../overview/TicketsGrid';
import { BOOKING_RECORD_REGISTRY } from '../registry';

import { MONO_SX, type BookingDisplayContext } from './displays';
import { StagePanel } from './StagePanel';

/** The linked order on one line (never a card inside the card): ref → its page, with
 *  the order's state and size when the operator may read orders. */
export function OrderLine({ c }: { c: BookingDisplayContext }) {
  const { detail: d, links } = c;
  const { hasPerm } = usePermissions();
  const canRead = hasPerm('console_orders_view');
  const orderId = d.order_id;
  const { data, isLoading } = useQuery({
    queryKey: ['orders', 'detail', orderId],
    queryFn: () => ordersApi.retrieve(orderId!),
    enabled: orderId !== null && canRead,
    staleTime: 60_000,
  });
  if (!orderId && !d.order_ref) return null;
  const label = d.order_ref ?? `#${orderId}`;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
      <ReceiptLongOutlinedIcon aria-hidden sx={{ fontSize: 16, color: 'text.secondary' }} />
      <ValueActions actions={orderActions(d, links)}>
        {orderId ? (
          <Link component={RouterLink} to={`/orders/${orderId}`} underline="hover" sx={MONO_SX}>
            {label}
          </Link>
        ) : (
          <Box component="span" sx={MONO_SX}>
            {label}
          </Box>
        )}
      </ValueActions>
      {orderId && canRead && isLoading && <Skeleton variant="text" width={120} />}
      {data && (
        <Typography component="span" variant="body2" color="text.secondary">
          {data.booking_state_display}
          {data.active ? '' : ' · inactive'} · {data.booking_count} {data.booking_count === 1 ? 'booking' : 'bookings'}
        </Typography>
      )}
      {!orderId && (
        <Typography component="span" variant="body2" color="text.secondary">
          no order with this ref
        </Typography>
      )}
    </Box>
  );
}

function Disclosure({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <Box>
      <Button
        size="small"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={id}
        endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ px: 0.75 }}
      >
        {label}
      </Button>
      <Collapse in={open} unmountOnExit>
        <Box id={id} sx={{ pt: 1 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

function LinkList({ c }: { c: BookingDisplayContext }) {
  const d = c.detail;
  const items = [
    { label: 'Deep link', url: d.branchio_link },
    { label: 'Guide self-registration', url: d.guide_sr_link },
    { label: 'Self-registration', url: d.sr_link },
  ].filter((i): i is { label: string; url: string } => Boolean(i.url));
  if (items.length === 0) return null;
  return (
    <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ typography: 'body2' }}>
      {items.map((i) => (
        <ValueActions key={i.label} actions={[act.external(i.url, 'Open'), act.copy(i.url, `Copy ${i.label.toLowerCase()}`)]}>
          <Link href={i.url} target="_blank" rel="noopener noreferrer" underline="hover">
            {i.label}
          </Link>
        </ValueActions>
      ))}
    </Stack>
  );
}

const isMoney = (f: ViewFieldRef) =>
  BOOKING_RECORD_REGISTRY[f.key as keyof typeof BOOKING_RECORD_REGISTRY]?.facet === 'money';

const hasKey = (section: ViewSection<BookingRecordStage>, key: string) =>
  section.blocks.some((b) => b.fields.some((f) => f.key === key));

export function BookedPanel({
  section,
  display,
}: {
  section: ViewSection<BookingRecordStage>;
  display: BookingDisplayContext;
}) {
  const d = display.detail;
  const related = d.family.length > 1;
  return (
    <StagePanel
      section={section}
      display={display}
      displays={{ order: <OrderLine c={display} /> }}
      groupVendor={(fields) => {
        const identity = fields.filter((f) => !isMoney(f));
        return [
          { id: 'identity', fields: identity },
          // Named only beside the booking's identity (the Money tab is the money alone).
          { id: 'money', ...(identity.length > 0 ? { title: 'Money' } : {}), fields: fields.filter(isMoney) },
        ];
      }}
      afterVendor={
        d.tickets.length > 0 && hasKey(section, 'tickets') ? (
          <Disclosure label={`Tickets (${d.tickets.length})`}>
            <TicketsGrid tickets={d.tickets} />
          </Disclosure>
        ) : undefined
      }
      afterSystem={
        hasKey(section, 'order') && (related || d.package.length > 0 || d.branchio_link || d.guide_sr_link || d.sr_link) ? (
          <Box sx={{ display: 'grid', gap: 0.5 }}>
            <LinkList c={display} />
            {related && (
              <Disclosure label={`Related bookings (${d.family.length})`}>
                <BookingFamilyTree nodes={d.family} ctx={display.links} />
              </Disclosure>
            )}
            {d.package.length > 0 && (
              <Disclosure label={`Package bookings (${d.package.length})`}>
                <PackageBookingsGrid bookings={d.package} ctx={display.links} />
              </Disclosure>
            )}
          </Box>
        ) : undefined
      }
    />
  );
}
